import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { LifeBuoy, ArrowLeft, Send, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
export const openSupport = (ticketId) => window.dispatchEvent(new CustomEvent('restosupply:open-support', { detail: { ticketId } }));

// In-app support inbox: users open tickets that admins answer from the admin panel.
const SupportButton = () => {
  const { user, getAuthHeader } = useAuth();
  const headers = useCallback(() => ({ headers: { Authorization: getAuthHeader() } }), [getAuthHeader]);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [view, setView] = useState('list'); // list | new | thread
  const [thread, setThread] = useState(null);
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');

  const fetchUnread = useCallback(() => axios.get(`${API}/support/unread`, headers()).then((r) => setUnread(r.data.unread || 0)).catch(() => {}), [headers]);
  const fetchTickets = useCallback(() => axios.get(`${API}/support/tickets`, headers()).then((r) => setTickets(r.data)).catch(() => {}), [headers]);
  const openThread = useCallback((id) => axios.get(`${API}/support/tickets/${id}`, headers()).then((r) => { setThread(r.data); setView('thread'); fetchUnread(); }).catch(() => toast.error('No se pudo abrir la consulta')), [headers, fetchUnread]);

  useEffect(() => {
    if (!user) return undefined;
    fetchUnread();
    const t = setInterval(fetchUnread, 20000);
    return () => clearInterval(t);
  }, [user, fetchUnread]);

  useEffect(() => {
    if (!open) return undefined;
    fetchTickets();
    const t = setInterval(() => { fetchTickets(); if (view === 'thread' && thread) openThread(thread.ticket.id); }, 6000);
    return () => clearInterval(t);
  }, [open, view, thread, fetchTickets, openThread]);

  useEffect(() => {
    const h = (e) => { setOpen(true); if (e.detail?.ticketId) openThread(e.detail.ticketId); else setView('list'); };
    window.addEventListener('restosupply:open-support', h);
    return () => window.removeEventListener('restosupply:open-support', h);
  }, [openThread]);

  const create = async () => {
    try {
      const r = await axios.post(`${API}/support/tickets`, { subject, message: text }, headers());
      setSubject(''); setText('');
      toast.success('Consulta enviada. Te responderemos en la app.');
      openThread(r.data.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo enviar'); }
  };

  const reply = async () => {
    if (!text.trim() || !thread) return;
    try {
      await axios.post(`${API}/support/tickets/${thread.ticket.id}/messages`, { text }, headers());
      setText('');
      openThread(thread.ticket.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo enviar'); }
  };

  if (!user) return null;

  return (
    <>
      <Button variant="ghost" size="sm" data-testid="support-button" onClick={() => { setView('list'); setOpen(true); }} title="Soporte" className="relative text-slate-600 hover:text-slate-900">
        <LifeBuoy size={20} />
        {unread > 0 && <span data-testid="support-unread-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="support-dialog" className="max-w-lg p-0 overflow-hidden h-[75vh] flex flex-col">
          <DialogHeader className="p-4 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-2">
              {view !== 'list' && <button type="button" data-testid="support-back" onClick={() => setView('list')} className="text-slate-500 hover:text-slate-900"><ArrowLeft size={18} /></button>}
              {view === 'thread' ? thread?.ticket.subject : view === 'new' ? 'Nueva consulta' : 'Soporte RestoSupply'}
            </DialogTitle>
            <DialogDescription>{view === 'list' ? 'Contacta con el equipo de administración desde la app' : view === 'new' ? 'Cuéntanos en qué podemos ayudarte' : (thread?.ticket.status === 'resolved' ? 'Consulta resuelta' : 'Consulta abierta')}</DialogDescription>
          </DialogHeader>

          {view === 'list' && (
            <div className="flex-1 overflow-y-auto" data-testid="support-ticket-list">
              <div className="p-3"><Button onClick={() => { setText(''); setView('new'); }} data-testid="support-new-button" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"><Plus size={16} className="mr-1" /> Nueva consulta</Button></div>
              {tickets.length === 0 ? <p className="p-6 text-sm text-slate-500 text-center">Aún no has abierto ninguna consulta.</p> : tickets.map((t) => (
                <button key={t.id} type="button" data-testid={`support-ticket-${t.id}`} onClick={() => openThread(t.id)} className="w-full text-left p-3 border-t border-slate-50 hover:bg-slate-50 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{t.subject}</p>
                    <p className="text-xs text-slate-500 truncate">{t.last_message}</p>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${t.status === 'resolved' ? 'bg-slate-100 text-slate-600' : 'bg-emerald-100 text-emerald-800'}`}>{t.status === 'resolved' ? 'Resuelta' : 'Abierta'}</span>
                  {t.unread > 0 && <span className="min-w-[20px] h-5 px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center">{t.unread}</span>}
                </button>
              ))}
            </div>
          )}

          {view === 'new' && (
            <div className="p-4 space-y-3">
              <Input data-testid="support-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Asunto" maxLength={120} />
              <textarea data-testid="support-message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Describe tu consulta o incidencia…" rows={6} className="w-full rounded-lg border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              <Button onClick={create} disabled={!subject.trim() || !text.trim()} data-testid="support-send-ticket" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"><Send size={16} className="mr-1" /> Enviar consulta</Button>
            </div>
          )}

          {view === 'thread' && thread && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50" data-testid="support-messages">
                {thread.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.from_admin ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${m.from_admin ? 'bg-white border border-slate-200 text-slate-900 rounded-bl-sm' : 'bg-emerald-600 text-white rounded-br-sm'}`}>
                      <p className="text-[10px] font-semibold opacity-80 mb-0.5">{m.from_admin ? 'Soporte RestoSupply' : 'Tú'}</p>
                      <p className="whitespace-pre-wrap break-words">{m.text}</p>
                      <p className={`text-[10px] mt-1 ${m.from_admin ? 'text-slate-400' : 'text-emerald-100'}`}>{fmt(m.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-slate-100 flex gap-2">
                <input data-testid="support-reply-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); reply(); } }} placeholder="Escribe una respuesta…" className="flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                <Button onClick={reply} disabled={!text.trim()} data-testid="support-reply-send" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4"><Send size={16} /></Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SupportButton;
